import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { SelectField } from '@/components/ui/select';
import { StepSection } from '@/components/step-section';
import { ApiError } from '@/lib/api';
import { use_draft } from '@/features/onboarding/hooks';
import { next_module_step_after } from '@/features/onboarding/module-step-router';
import {
  use_cremation_details,
  use_save_cremation_details,
} from '@/features/cremation/hooks';
import {
  CREMATION_SERVICE_OPTIONS,
  CREMATION_SERVICE_RADIUS_OPTIONS,
  type CremationServiceRadius,
  type CremationServiceType,
} from '@/features/cremation/types';

type Form = {
  service_radius: CremationServiceRadius | '';
  home_pickup_available: boolean;
  is_24x7_emergency: boolean;
  cremation_services: CremationServiceType[];
};

const EMPTY: Form = {
  service_radius: '',
  home_pickup_available: false,
  is_24x7_emergency: false,
  cremation_services: [],
};

function toggle<T>(arr: T[], value: T): T[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

/** Keys of `Form` whose value is an array — the multi-select pill fields. */
type ArrayKeys<T> = { [K in keyof T]: T[K] extends unknown[] ? K : never }[keyof T];

export function ModuleCremationPage() {
  const navigate = useNavigate();
  const draft_q = use_draft();
  const saved_q = use_cremation_details({ enabled: !!draft_q.data });
  const save_m = use_save_cremation_details();
  const [form, set_form] = useState<Form>(EMPTY);
  const [prefilled, set_prefilled] = useState(false);
  const [error, set_error] = useState<string | null>(null);

  useEffect(() => {
    if (!draft_q.isFetched) return;
    if (!draft_q.data) {
      navigate('/onboarding/start', { replace: true });
      return;
    }
    if (!draft_q.data.address_completed) {
      navigate('/onboarding/address', { replace: true });
      return;
    }
    if (!draft_q.data.module_types.includes('CREMATION')) {
      navigate(next_module_step_after('CREMATION', draft_q.data.module_types), {
        replace: true,
      });
    }
  }, [draft_q.isFetched, draft_q.data, navigate]);

  useEffect(() => {
    if (prefilled) return;
    if (saved_q.data) {
      set_form({
        service_radius: saved_q.data.service_radius ?? '',
        home_pickup_available: saved_q.data.home_pickup_available,
        is_24x7_emergency: saved_q.data.is_24x7_emergency,
        cremation_services: saved_q.data.cremation_services,
      });
      set_prefilled(true);
    }
  }, [saved_q.data, prefilled]);

  function update<K extends keyof Form>(key: K, value: Form[K]) {
    set_form((f) => ({ ...f, [key]: value }));
  }

  /**
   * Toggle a pill *inside* the updater. Computing `toggle(form.x, v)` at the call
   * site reads the render closure, so several picks landing in one React batch
   * all start from the same stale array and only the last one survives.
   */
  function toggle_in<K extends ArrayKeys<Form>>(key: K, value: Form[K][number]) {
    set_form((f) => ({ ...f, [key]: toggle(f[key] as Form[K][number][], value) }));
  }

  async function handle_submit(e: React.FormEvent) {
    e.preventDefault();
    set_error(null);

    if (form.cremation_services.length < 1) {
      set_error('Pick at least one cremation service you offer.');
      return;
    }
    if (form.cremation_services.length > 8) {
      set_error('At most 8 cremation services.');
      return;
    }

    try {
      await save_m.mutateAsync({
        service_radius: form.service_radius === '' ? undefined : form.service_radius,
        home_pickup_available: form.home_pickup_available,
        is_24x7_emergency: form.is_24x7_emergency,
        cremation_services: form.cremation_services,
      });
      if (!draft_q.data) return;
      navigate(next_module_step_after('CREMATION', draft_q.data.module_types));
    } catch (err) {
      set_error(
        err instanceof ApiError ? err.message : 'Could not save cremation details',
      );
    }
  }

  return (
    <form onSubmit={handle_submit}>
      <StepSection
        step_key="module-cremation"
        title="Cremation service details"
        description="Your service area, availability, and the cremation options you offer."
        error={error}
        footer={
          <Button
            type="submit"
            variant="gradient"
            disabled={save_m.isPending}
            data-testid="module-cremation-submit"
          >
            {save_m.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save & continue
          </Button>
        }
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="service_radius">Service radius</Label>
          <SelectField
            id="service_radius"
            data-testid="field-cremation-radius"
            value={form.service_radius}
            onValueChange={(v) => update('service_radius', v as CremationServiceRadius | '')}
            options={[
              { value: '', label: '— Not specified —' },
              ...CREMATION_SERVICE_RADIUS_OPTIONS,
            ]}
          />
        </div>

        <div className="flex flex-col gap-3 rounded-lg border border-border bg-card/60 p-4">
          <label
            className="flex items-center gap-2 text-sm"
            data-testid="field-cremation-home-pickup-label"
          >
            <input
              type="checkbox"
              data-testid="field-cremation-home-pickup"
              checked={form.home_pickup_available}
              onChange={(e) => update('home_pickup_available', e.target.checked)}
              className="h-4 w-4 rounded border-input accent-primary"
            />
            We offer home pickup
          </label>
          <label
            className="flex items-center gap-2 text-sm"
            data-testid="field-cremation-24x7-label"
          >
            <input
              type="checkbox"
              data-testid="field-cremation-24x7"
              checked={form.is_24x7_emergency}
              onChange={(e) => update('is_24x7_emergency', e.target.checked)}
              className="h-4 w-4 rounded border-input accent-primary"
            />
            We provide 24×7 emergency service
          </label>
        </div>

        <div className="flex flex-col gap-2">
          <Label>Cremation services offered (pick 1–8)</Label>
          <div className="flex flex-wrap gap-2" data-testid="cremation-services">
            {CREMATION_SERVICE_OPTIONS.map((s) => {
              const active = form.cremation_services.includes(s.value);
              return (
                <button
                  key={s.value}
                  type="button"
                  data-testid={`cremation-service-${s.value}`}
                  data-checked={active}
                  onClick={() =>
                    toggle_in('cremation_services', s.value)
                  }
                  className={
                    'rounded-full border px-3 py-1.5 text-sm transition-colors ' +
                    (active
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-input bg-card text-foreground hover:bg-muted')
                  }
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
      </StepSection>
    </form>
  );
}
